// library imports
import * as React from "react";
import {
  Link,
  Container,
  Box,
  Radio,
  Typography,
  Button,
  Paper,
  InputBase,
  Drawer,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import ProfileMenu from "./ProfileMenu";
import LocationCityOutlinedIcon from "@mui/icons-material/LocationCityOutlined";

// icons imports
import HomeIcon from "@mui/icons-material/Home";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import PersonIcon from "@mui/icons-material/Person";
import ExitToAppIcon from "@mui/icons-material/ExitToApp";
import EmailIcon from "@mui/icons-material/Email";
import LibraryBooksIcon from "@mui/icons-material/LibraryBooks";
import SettingsIcon from "@mui/icons-material/Settings";
import SearchIcon from "@mui/icons-material/Search";

// style and other app setting imports
import { useHeaderStyles } from "../styles/useHeaderStyles";
import { getHeaders } from "../utils/HeaderHelper";
import { AuthenticationContext } from "../Context";
import { useFlash } from "../FlashContext";


interface Props {
  small_logo_src: string;
  logo_src: string;
  user_id: number;
  patient_id: number;
  csrfToken: any;
  customers: any;
  selected_customer: any;
  sync_device: string;
  user: object;
  refreshUser: boolean;
  setRefreshUser: any;
}

const SHOW_TERMS_AND_CONDITIONS = false;

const Header: React.FC<Props> = (props: any) => {
  //  context import
  const authenticationSetting = React.useContext(AuthenticationContext);

  // other states and styles
  const { classes } = useHeaderStyles();
  const [showMobileMenu, setShowMobileMenu] = React.useState(false);
  const [user, setUser] = React.useState<any>({});
  const [organization, setOrganization] = React.useState<any>(null);
  const [tempSelectedOrg, setTempSelectedOrg] = React.useState(
    props.selected_customer
  );
  const disableSelection = props.customers?.length > 1 ? false : true;

  const [showModal, setShowModal] = React.useState(false);
  const [searchTerm, setSearchTerm] = React.useState("");
  const { showToast } = useFlash();

  const handleModalOpen = () => {
    setSelectedOrganization();
    setShowModal(true);
  };

  const handleModalClose = () => {
    setShowModal(false);
  };

  React.useEffect(() => {
    const fetchData = async () => {
      try {
        const result = await fetch(
          `/data_fetching/users/basic_user_info/${props.user_id}`,
          {
            method: "GET",
            headers: getHeaders(authenticationSetting.csrfToken),
          }
        );
        const data = await result.json();
        if (data.success) {
          setUser(data?.resource);
        }
      } catch (error) {
        console.error(error);
      } finally {
        if (props.refreshUser) {
          props.setRefreshUser(false);
        }
      }
    };

    fetchData();
  }, [props.user_id, props.refreshUser]);

  const currentDate = () => {
    var weekday = [
      "Sunday",
      "Monday",
      "Tuesday",
      "Wednesday",
      "Thursday",
      "Friday",
      "Saturday",
    ];
    const monthName = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ];

    var today = new Date();

    return (
      weekday[today.getDay()] +
      ", " +
      monthName[today.getMonth()] +
      " " +
      today.getDate() +
      ", " +
      today.getFullYear()
    );
  };

  const updateCustomer = (new_customer_id) => {
    fetch(`/customer_selections/${props.selected_customer.id}`, {
      method: "PATCH",
      headers: getHeaders(authenticationSetting.csrfToken),
      body: JSON.stringify({
        new_customer_id: new_customer_id,
      }),
    })
      .then((result) => result.json())
      .then((result) => {
        if (typeof result.error !== "undefined") {
          // Replace console.log with a direct error toast since we haven't redirected yet
          showToast({ 
            title: "Selection Error", 
            text: result.error, 
            type: "error" 
          });
        } else {
          sessionStorage.setItem("pendingToast", JSON.stringify({
            title: "Customer Switched",
            text: `Now viewing data for the selected customer.`,
            type: "success",
            autoHideDuration: 4000
          }));

          if (window.location.pathname == "/care-plan-management") {
            window.location.href = window.location.pathname;
          } else {
            window.location.reload();
          }
        }
      })
      .catch((error) => {
        showToast({ 
          title: "System Error", 
          text: "Failed to update customer selection.", 
          type: "error" 
        });
      });
  };

  const setSelectedOrganization = () => {
    if (props.selected_customer && props.customers) {
      const selectedOrg = props.customers.find(
        (c) => c.id === props.selected_customer.customer_id
      );
      setTempSelectedOrg(selectedOrg || null);
      setOrganization(selectedOrg || null);
    }
  };

  React.useEffect(() => {
    setSelectedOrganization();
  }, [props.selected_customer, props.customers]);

  return (
    <Container maxWidth={false}>
      <div className={classes.desktop}>
        <div className={classes.header}>
          <div
            className={classes.welcomeText}
            style={{ display: "flex", alignItems: "center" }}
          >
            <div className={classes.welcomeLeft}>
              <img
                className={classes.logo}
                src={props.small_logo_src}
                alt="Project Starfield"
                style={{ width: 45, marginTop: 4 }}
              />
            </div>
            <div className={classes.welcomeRight} style={{ marginTop: "0px" }}>
              {user?.name} <br />
              <span className={classes.welcomeSmall}>{currentDate()}</span>
            </div>

            {user?.role !== "patient" && (
              <div>
                <Button
                  startIcon={<LocationCityOutlinedIcon />}
                  onClick={handleModalOpen}
                  disabled={disableSelection}
                  sx={{
                    textTransform: "none",
                    textDecoration: disableSelection ? "none" : "underline",
                    fontSize: "13px",
                    color: "white",
                    backgroundColor: "#FF890A",
                    marginLeft: "30px",
                    pointerEvents: disableSelection ? "none" : "auto",
                    "&.Mui-disabled": {
                      color: "white",
                      backgroundColor: "#FF890A",
                    },
                  }}
                >
                  {organization?.name || "Select Organization"}
                </Button>

                <Drawer
                  anchor="right" // slide in from the right
                  open={showModal}
                  onClose={handleModalClose}
                  PaperProps={{
                    sx: { width: 430, padding: 0, height: "100%" },
                  }}
                >
                  <div
                    className="drawerContent"
                    style={{
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <div
                      className="drawerHeader"
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        padding: "24px",
                        borderBottom: "2px solid #ff890a",
                      }}
                    >
                      <Typography sx={{ fontSize: "21px", fontWeight: 700 }}>
                        Select an Organization
                      </Typography>
                      <CloseIcon
                        sx={{ cursor: "pointer" }}
                        onClick={handleModalClose}
                      />
                    </div>

                    <Box
                      sx={{
                        padding: "20px 24px",
                        display: "flex",
                        flexDirection: "column",
                        rowGap: "16px",
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: "12px",
                          lineHeight: "14px",
                          fontWeight: 500,
                          color: "black",
                        }}
                      >
                        Use the search field below to find the organization you
                        would like to access
                      </Typography>

                      <Paper
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          backgroundColor: "#EFE9E7",
                          padding: "0px 16px",
                          height: "42.5px",
                          boxShadow: "none",
                        }}
                      >
                        <InputBase
                          placeholder="Search Organization Name"
                          inputProps={{
                            "aria-label": "search organization name",
                          }}
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          sx={{ width: "100%" }}
                        />
                        <SearchIcon sx={{ color: "#707070" }} />
                      </Paper>
                    </Box>

                    <Box
                      sx={{
                        padding: "12px",
                        backgroundColor: "#EFE9E7",
                        display: "flex",
                        flexDirection: "column",
                        gap: "12px",
                        overflowY: "auto",
                      }}
                    >
                      {props.customers
                        ?.filter((org) =>
                          org.name
                            .toLowerCase()
                            .includes(searchTerm.toLowerCase())
                        )
                        .sort((a, b) => a.name.localeCompare(b.name))
                        .map((org) => {
                          const isSelected = tempSelectedOrg?.id === org.id;

                          return (
                            <Box
                              key={org.id}
                              sx={{
                                backgroundColor: "white",
                                borderRadius: "4px",
                                padding: "14px 12px",
                                display: "flex",
                                alignItems: "center",
                                gap: "12px",
                                cursor: "pointer",
                              }}
                              onClick={() => setTempSelectedOrg(org)}
                            >
                              <Radio
                                checked={isSelected}
                                color="primary"
                                size="small"
                                sx={{ padding: "0px" }}
                              />
                              <Box>
                                <Typography
                                  sx={{
                                    fontSize: "14px",
                                    lineHeight: "18px",
                                    fontWeight: 700,
                                    color: "#4A4442",
                                    letterSpacing: "1px",
                                  }}
                                >
                                  {org.name}
                                </Typography>
                                <Typography
                                  sx={{
                                    fontSize: "10px",
                                    lineHeight: "18px",
                                    fontWeight: 500,
                                    color: "#707070",
                                    letterSpacing: "1px",
                                  }}
                                >
                                  {`${org.address ? org.address + ", " : ""}${
                                    org.city ? org.city + ", " : ""
                                  }${org.state ? org.state + " " : ""}${
                                    org.zip ? org.zip : ""
                                  }` || "N/A"}
                                </Typography>
                              </Box>
                            </Box>
                          );
                        })}
                    </Box>

                    <Box sx={{ padding: "20px 16px" }}>
                      <Button
                        variant="contained"
                        fullWidth
                        onClick={() => {
                          if (
                            tempSelectedOrg &&
                            tempSelectedOrg.id !== props.selected_customer?.id
                          ) {
                            updateCustomer(tempSelectedOrg.id);
                          }
                          handleModalClose();
                        }}
                      >
                        Switch Organization
                      </Button>
                    </Box>
                  </div>
                </Drawer>
              </div>
            )}
          </div>
          <ProfileMenu user={user}></ProfileMenu>
        </div>
      </div>

      <div className={classes.mobile}>
        <div className={classes.mobileHeader}>
          <div className={classes.mobileMenuLink}>
            {!showMobileMenu && (
              <span
                className={classes.mobileNavLink}
                onClick={(event) => {
                  event.preventDefault();
                  setShowMobileMenu(true);
                }}
              >
                <MenuIcon className={classes.mobileNavIconBlack} />
              </span>
            )}
            {showMobileMenu && (
              <span
                className={classes.mobileNavLink}
                onClick={(event) => {
                  event.preventDefault();
                  setShowMobileMenu(false);
                }}
              >
                <CloseIcon className={classes.mobileNavIconBlack} />
              </span>
            )}
          </div>
          <div className={classes.mobileLogo}>
            <img
              className={classes.mobileLogoImage}
              src={props.logo_src}
              style={{ width: 32, marginTop: 5 }}
              alt="Project Starfield"
            />
          </div>
          <div className={classes.mobileHomeLink}>
            <Link
              href={user?.role == "patient" ? "/?menu=false" : "/"}
              className={classes.mobileNavLink}
            >
              <HomeIcon
                className={classes.mobileNavIcon}
                style={{ color: user?.role == "patient" ? "#f78204" : "" }}
              />
            </Link>
          </div>
        </div>
        {showMobileMenu && (
          <div className={classes.mobileNavigation}>
            {user?.role !== "patient" && (
              <>
                <RouterLink
                  to="/patients"
                  className={classes.mobileNavigationLink}
                  onClick={(event) => {
                    setShowMobileMenu(false);
                  }}
                >
                  Patients
                </RouterLink>
              </>
            )}
            {user?.role == "provider" && user?.career == "pharmacist" && (
              <>
                <RouterLink
                  to="/add-a-patient"
                  className={classes.mobileNavigationLink}
                  onClick={(event) => {
                    setShowMobileMenu(false);
                  }}
                >
                  Invite a Patient
                </RouterLink>
                <Link
                  href="https://hie.ndhin.com/concerto/Login.htm"
                  target="_blank"
                  className={classes.mobileNavigationLink}
                >
                  Orion Health Login
                </Link>
              </>
            )}
            {user?.role != "patient" && props.patient_id && (
              <>
                <RouterLink
                  to={`/data-rpm/${props.patient_id}`}
                  className={classes.mobileNavigationLink}
                  onClick={(event) => {
                    setShowMobileMenu(false);
                  }}
                >
                  RPM
                </RouterLink>
                <RouterLink
                  to={`/care-plan-management`}
                  className={classes.mobileNavigationLink}
                  onClick={(event) => {
                    setShowMobileMenu(false);
                  }}
                >
                  Care Plan Management
                </RouterLink>
                <RouterLink
                  to={`/resource-catelog`}
                  className={classes.mobileNavigationLink}
                  onClick={(event) => {
                    setShowMobileMenu(false);
                  }}
                >
                  Resource Catalog
                </RouterLink>
              </>
            )}
            {user?.role == "patient" && (
              <>
                <RouterLink
                  to="/sync-device"
                  className={classes.mobileNavigationLink}
                  onClick={(event) => {
                    setShowMobileMenu(false);
                  }}
                >
                  <img
                    src={props.sync_device}
                    style={{
                      float: "left",
                      marginTop: 0,
                      marginRight: 10,
                      width: 22,
                    }}
                  />
                  <span style={{ float: "left" }}>Sync My Device</span>
                </RouterLink>
                <RouterLink
                  to="/my-info"
                  className={classes.mobileNavigationLink}
                  onClick={(event) => {
                    setShowMobileMenu(false);
                  }}
                >
                  <PersonIcon
                    style={{ float: "left", marginTop: 0, marginRight: 10 }}
                  />
                  <span style={{ float: "left" }}>My Info</span>
                </RouterLink>
                <RouterLink
                  to="/preferences"
                  className={classes.mobileNavigationLink}
                  onClick={(event) => {
                    setShowMobileMenu(false);
                  }}
                >
                  <SettingsIcon
                    style={{ float: "left", marginTop: 0, marginRight: 10 }}
                  />
                  <span style={{ float: "left" }}>Preferences</span>
                </RouterLink>
                <RouterLink
                  to="/contact-us"
                  className={classes.mobileNavigationLink}
                  onClick={(event) => {
                    setShowMobileMenu(false);
                  }}
                >
                  <EmailIcon
                    style={{ float: "left", marginTop: 0, marginRight: 10 }}
                  />{" "}
                  <span style={{ float: "left" }}>Contact Us</span>
                </RouterLink>

                {SHOW_TERMS_AND_CONDITIONS && (
                  <RouterLink
                    to="/terms"
                    className={classes.mobileNavigationLink}
                    onClick={(event) => {
                      setShowMobileMenu(false);
                    }}
                  >
                    <LibraryBooksIcon
                      style={{ float: "left", marginTop: 0, marginRight: 10 }}
                    />{" "}
                    <span style={{ float: "left" }}>Terms & Conditions</span>
                  </RouterLink>
                )}
              </>
            )}
            <a
              className={classes.mobileNavigationLink}
              rel="nofollow"
              data-method="delete"
              href="/users/sign_out"
            >
              <ExitToAppIcon
                style={{ float: "left", marginTop: 0, marginRight: 10 }}
              />{" "}
              <span style={{ float: "left" }}>Log Out</span>
            </a>
          </div>
        )}
      </div>
    </Container>
  );
};

export default Header;
